import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Check,
  MapPin,
  Phone,
  Shield,
  Hammer,
  Trash2,
  Ruler,
  Droplets,
  Award,
} from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import MobileStickyCTA from "@/components/site/MobileStickyCTA";
import QuoteContact from "@/components/site/QuoteContact";
import Testimonials from "@/components/site/Testimonials";

const IMG = {
  hero: "https://customer-assets.emergentagent.com/job_hardscape-pro-5/artifacts/5fuiwjzx_IMG_7766.jpeg",
  lvp: "https://customer-assets.emergentagent.com/job_hardscape-pro-5/artifacts/ud9ci9kn_2F372B81-94D2-480F-B9D3-8B7BE4993A4D.png",
  laminate:
    "https://customer-assets-eiarnc6j.emergentagent.net/job_hardscape-pro-5/artifacts/4wknacka_IMG_8732.jpeg",
  tile: "https://customer-assets.emergentagent.com/job_hardscape-pro-5/artifacts/9z6zytzm_IMG_7768.jpeg",
  removal:
    "https://customer-assets-eiarnc6j.emergentagent.net/job_hardscape-pro-5/artifacts/uzklex8i_IMG_8394.jpeg",
  baseboard:
    "https://customer-assets-eiarnc6j.emergentagent.net/job_hardscape-pro-5/artifacts/bd1eb7qe_IMG_8258.jpeg",
};

const SERVICES = [
  {
    id: "lvp",
    icon: Droplets,
    title: "Luxury Vinyl Plank (LVP) Installation",
    subtitle: "100% waterproof · scratch-resistant · pet & kid friendly",
    image: IMG.lvp,
    body: [
      "LVP is our most-requested flooring across the St. Louis metro — and for good reason. It's completely waterproof, incredibly durable, and virtually indistinguishable from real hardwood. Homeowners in Chesterfield, Ballwin, Kirkwood, and Webster Groves love LVP for open-plan main levels; families in O'Fallon, Wentzville, and St. Peters choose it for basements, kitchens, and mudrooms where moisture and heavy foot traffic are a daily reality.",
      "Every M&L LVP install starts with a level, moisture-sealed subfloor. We score every plank on-site for tight seams and flush transitions across doorways, kitchens, and hallways.",
    ],
    features: [
      "Wide-plank & standard-width options",
      "Waterproof click-lock or glue-down systems",
      "Level subfloor prep + moisture barrier",
      "Precision cuts at doorways, vents & transitions",
    ],
  },
  {
    id: "laminate",
    icon: Hammer,
    title: "Laminate Flooring Installation",
    subtitle: "The hardwood look for less · scratch & fade resistant",
    image: IMG.laminate,
    body: [
      "Laminate gives you the warmth and look of real hardwood at a fraction of the cost — and its ultra-hard wear layer actually outperforms real wood against scratches, fading, and daily wear. It's a smart, elegant choice for living rooms, bedrooms, hallways, and dining rooms across St. Louis and St. Charles County.",
      "From starter homes in Florissant, Hazelwood, and Bridgeton to remodels in Clayton, Creve Coeur, and Ladue, we install laminate with the same obsessive attention to subfloor prep and seam alignment that we put into every job.",
    ],
    features: [
      "Wood-look laminate in dozens of finishes",
      "Extra-hard wear layer for high traffic",
      "Fast, clean, low-dust installation",
      "Perfect for whole-home or single-room remodels",
    ],
  },
  {
    id: "tile",
    icon: Ruler,
    title: "Tile Installation · Bathrooms, Kitchens & Entryways",
    subtitle: "Hex · Marble-look · Herringbone · Mosaic",
    image: IMG.tile,
    body: [
      "Tile is where our precision really shows. From large-format marble-look tile in a Chesterfield primary bath to intricate hex mosaics in a Lafayette Square powder room, every cut is scored on-site for tight grout lines and flush transitions. We handle kitchen backsplashes, shower walls, foyers, and full-floor installs.",
      "Popular St. Louis picks: matte porcelain in Kirkwood, herringbone marble in Central West End remodels, and durable 12×24 large-format tile for family homes in Wentzville, O'Fallon, and Lake St. Louis.",
    ],
    features: [
      "Ceramic · porcelain · natural stone · mosaic",
      "Custom patterns: hex, herringbone, chevron",
      "Waterproof shower & wet-area builds",
      "Tight grout lines and flush thresholds",
    ],
  },
  {
    id: "removal",
    icon: Trash2,
    title: "Flooring Removal & Subfloor Prep",
    subtitle: "Carpet · old laminate · tile · glued-down vinyl",
    image: IMG.removal,
    body: [
      "Old flooring hides a lot — cupped subfloor, moisture damage, uneven leveling. Before any new install, we do a clean, complete tear-out: carpet, padding, staples, glued-down vinyl, cracked tile, worn laminate — all removed and hauled away.",
      "Then we level, patch, and moisture-seal the subfloor so your new LVP, laminate, or tile installs on a rock-solid, engineered surface. This is the single most important step for flooring that lasts — and the one most contractors cut corners on.",
    ],
    features: [
      "Carpet, tack strip & staple removal",
      "Old laminate, LVP, and tile tear-out",
      "Subfloor leveling & moisture sealing",
      "Full haul-away & site cleanup included",
    ],
  },
  {
    id: "baseboards",
    icon: Ruler,
    title: "Baseboard Installation & Trim Work",
    subtitle: "Clean finish for every new floor · shoe molding · quarter round",
    image: IMG.baseboard,
    body: [
      "A new floor is only as clean as its edges. We install and re-set baseboards, shoe molding, and quarter round after every flooring project — with tight mitered corners, caulked seams, and a factory-finish touch-up.",
      "From modern flat 5-inch baseboards popular in newer St. Charles County builds to classic profiled trim in older Kirkwood, Webster Groves, and Soulard homes, we match the style your home was built for.",
    ],
    features: [
      "Modern flat & profiled traditional trim",
      "Tight mitered corners, caulked & painted",
      "Shoe molding & quarter round options",
      "Re-installation of existing baseboards",
    ],
  },
];

const NEIGHBORHOODS_STL_COUNTY = [
  "Chesterfield",
  "Clayton",
  "Kirkwood",
  "Webster Groves",
  "Ballwin",
  "Ellisville",
  "Wildwood",
  "Creve Coeur",
  "Town and Country",
  "Ladue",
  "Frontenac",
  "Des Peres",
  "Sunset Hills",
  "Maryland Heights",
  "University City",
  "Florissant",
  "Hazelwood",
  "Bridgeton",
  "Manchester",
  "Fenton",
  "Arnold",
  "Oakville",
  "Affton",
];

const NEIGHBORHOODS_STL_CITY = [
  "Central West End",
  "The Hill",
  "Soulard",
  "Lafayette Square",
  "Tower Grove",
  "Shaw",
  "Benton Park",
  "South City",
  "Downtown St. Louis",
  "Dogtown",
  "Holly Hills",
  "Bevo Mill",
];

const NEIGHBORHOODS_STCHARLES = [
  "St. Charles",
  "O'Fallon",
  "Wentzville",
  "St. Peters",
  "Lake St. Louis",
  "Cottleville",
  "Dardenne Prairie",
  "Weldon Spring",
  "New Melle",
  "Augusta",
  "Foristell",
];

const FAQS = [
  {
    q: "Who is the best flooring contractor in St. Louis and St. Charles County?",
    a: "M&L Exterior and Interior Design has been installing premium LVP, laminate, and tile flooring across Greater St. Louis and all of St. Charles County since 2013. Owner-operated, fully licensed and insured, and rated 5.0 stars on Google. We serve every ZIP code from Chesterfield and Clayton to O'Fallon, Wentzville, and Lake St. Louis.",
  },
  {
    q: "Do you install LVP, laminate, and tile in the City of St. Louis?",
    a: "Yes — we install in the entire city and county. Popular neighborhoods we serve include Central West End, The Hill, Soulard, Lafayette Square, Tower Grove, Shaw, and Benton Park, plus every major community in St. Louis County (Kirkwood, Webster Groves, Ballwin, Ellisville, Wildwood, Creve Coeur) and St. Charles County (O'Fallon, St. Peters, Wentzville).",
  },
  {
    q: "Do you remove old flooring and haul it away?",
    a: "Yes. Every project includes complete tear-out — carpet, padding, staples, old laminate, LVP, glued-down vinyl, or cracked tile — plus full site cleanup and haul-away. We then level and moisture-seal the subfloor before installing your new floor.",
  },
  {
    q: "Do you also install baseboards and trim after the flooring?",
    a: "Yes. Baseboards, shoe molding, and quarter round are part of a proper flooring finish — we install, re-set, caulk, and touch up all trim so the finished floor reads clean from wall to wall.",
  },
  {
    q: "How much does flooring installation cost in the St. Louis area?",
    a: "Labor typically ranges from $3–$6 per sq ft for LVP and laminate, and $7–$12 per sq ft for tile — depending on subfloor prep, layout, transitions, and pattern complexity. We provide a transparent, line-item written estimate after a free on-site walkthrough.",
  },
  {
    q: "How long does a flooring install take?",
    a: "Most single-room LVP or laminate jobs finish in 1–2 days. Full-floor whole-home installs run 3–7 working days. Bathroom tile typically takes 3–5 days depending on layout and waterproofing complexity.",
  },
];

export default function FlooringContractor() {
  useEffect(() => {
    const prevTitle = document.title;
    const prevDesc = document
      .querySelector('meta[name="description"]')
      ?.getAttribute("content");
    const prevCanonical = document
      .querySelector('link[rel="canonical"]')
      ?.getAttribute("href");

    document.title =
      "Flooring Contractor St. Louis & St. Charles County | M&L";

    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        "content",
        "Premium flooring contractor serving St. Louis and all of St. Charles County — LVP, laminate, tile, flooring removal, and baseboard installation. Owner-operated. Licensed & insured. Free quotes across Chesterfield, Kirkwood, O'Fallon, Wentzville, St. Peters, and every neighborhood in between."
      );
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute(
        "href",
        "https://mlexteriordesign.com/flooring-contractor-st-louis"
      );
    }

    // Inject Service + FAQ JSON-LD schema
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "flooring-contractor-schema";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          "@id":
            "https://mlexteriordesign.com/flooring-contractor-st-louis#service",
          serviceType: "Flooring Contractor",
          name: "Flooring Contractor · St. Louis & St. Charles County",
          provider: { "@id": "https://mlexteriordesign.com/#business" },
          areaServed: [
            {
              "@type": "AdministrativeArea",
              name: "St. Louis County, Missouri",
            },
            {
              "@type": "AdministrativeArea",
              name: "St. Charles County, Missouri",
            },
            { "@type": "City", name: "St. Louis, Missouri" },
          ],
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Flooring Services",
            itemListElement: [
              {
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: "LVP Flooring Installation",
                },
              },
              {
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: "Laminate Flooring Installation",
                },
              },
              {
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: "Tile Installation",
                },
              },
              {
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: "Flooring Removal & Subfloor Prep",
                },
              },
              {
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: "Baseboard Installation & Trim",
                },
              },
            ],
          },
        },
        {
          "@type": "FAQPage",
          "@id":
            "https://mlexteriordesign.com/flooring-contractor-st-louis#faq",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://mlexteriordesign.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Flooring Contractor St. Louis",
              item: "https://mlexteriordesign.com/flooring-contractor-st-louis",
            },
          ],
        },
      ],
    });
    document.head.appendChild(script);

    window.scrollTo(0, 0);

    return () => {
      document.title = prevTitle;
      if (desc && prevDesc) desc.setAttribute("content", prevDesc);
      if (canonical && prevCanonical)
        canonical.setAttribute("href", prevCanonical);
      const s = document.getElementById("flooring-contractor-schema");
      if (s) s.remove();
    };
  }, []);

  return (
    <div
      className="min-h-screen bg-stone-50 text-stone-900"
      data-testid="flooring-contractor-page"
    >
      <Header />

      <main>
        {/* HERO */}
        <section
          data-testid="fc-hero"
          className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 bg-stone-900 text-stone-50 overflow-hidden"
        >
          <div
            aria-hidden
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `url(${IMG.hero})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <div
            aria-hidden
            className="absolute inset-0 bg-gradient-to-r from-stone-950 via-stone-950/80 to-transparent"
          />

          <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
            <nav
              aria-label="Breadcrumb"
              className="text-xs text-stone-400 mb-6"
              data-testid="fc-breadcrumb"
            >
              <Link to="/" className="hover:text-orange-400 transition-colors">
                Home
              </Link>{" "}
              <span className="text-stone-600">/</span>{" "}
              <span className="text-stone-200">
                Flooring Contractor · St. Louis
              </span>
            </nav>

            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-orange-400 font-semibold mb-6">
              <MapPin size={13} />
              <span>Serving Greater St. Louis · St. Charles County · Missouri</span>
            </div>

            <h1
              data-testid="fc-h1"
              className="font-display font-semibold text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-[-0.03em] max-w-4xl"
            >
              Flooring Contractor
              <br />
              <span className="text-orange-400">
                St. Louis &amp; St. Charles County
              </span>
            </h1>

            <p className="mt-6 text-lg lg:text-xl text-stone-300 leading-relaxed max-w-2xl">
              Premium <strong className="text-stone-100">LVP</strong>,{" "}
              <strong className="text-stone-100">laminate</strong>, and{" "}
              <strong className="text-stone-100">tile installation</strong> —
              plus complete <strong className="text-stone-100">flooring removal</strong> and{" "}
              <strong className="text-stone-100">baseboard finishing</strong>.
              Owner-operated. Licensed &amp; insured. Serving every neighborhood
              from Chesterfield and Kirkwood to O&apos;Fallon, Wentzville, St.
              Peters, and Central West End.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <a
                href="#contact"
                data-testid="fc-cta-quote"
                className="inline-flex items-center gap-2 px-6 py-4 bg-orange-500 hover:bg-orange-600 text-stone-50 text-sm font-semibold rounded-full transition-colors group"
              >
                Get a Free Quote
                <ArrowRight
                  size={16}
                  className="group-hover:translate-x-0.5 transition-transform"
                />
              </a>
              <a
                href="tel:+16368496681"
                data-testid="fc-cta-call"
                className="inline-flex items-center gap-2 px-6 py-4 bg-stone-50/10 backdrop-blur-sm border border-stone-50/20 hover:bg-stone-50/20 text-stone-50 text-sm font-semibold rounded-full transition-colors"
              >
                <Phone size={15} /> 636-849-6681
              </a>
            </div>

            {/* Trust badges */}
            <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 pt-10 border-t border-stone-800/60 max-w-3xl">
              {[
                { icon: Shield, label: "Licensed & Insured" },
                { icon: Award, label: "5.0 Google Rating" },
                { icon: Hammer, label: "Est. 2013" },
                { icon: MapPin, label: "40+ Cities Served" },
              ].map((b) => {
                const Icon = b.icon;
                return (
                  <div
                    key={b.label}
                    className="flex items-center gap-3 text-stone-300"
                  >
                    <Icon
                      size={18}
                      className="text-orange-400 shrink-0"
                      strokeWidth={1.75}
                    />
                    <span className="text-sm font-medium">{b.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* INTRO — Local Keyword Rich */}
        <section
          data-testid="fc-intro"
          className="py-16 lg:py-24 bg-white border-b border-stone-200"
        >
          <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-orange-600 font-semibold mb-6">
              <span className="h-px w-8 bg-orange-600" />
              Local Flooring Experts
            </div>
            <h2 className="font-display font-semibold text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-[-0.025em] text-stone-900 max-w-3xl">
              Your local flooring contractor across
              <br />
              <span className="text-stone-500">
                every ZIP code in St. Louis &amp; St. Charles County.
              </span>
            </h2>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8 text-[16px] leading-[1.75] text-stone-700">
              <p>
                M&amp;L Exterior and Interior Design is a locally owned,
                owner-operated flooring contractor based in{" "}
                <strong className="text-stone-900">O&apos;Fallon, Missouri</strong>{" "}
                — serving the entire Greater St. Louis metro area since 2013.
                We install <strong>LVP (Luxury Vinyl Plank)</strong>,{" "}
                <strong>laminate</strong>, and <strong>tile</strong> flooring
                across St. Louis City, St. Louis County, and all of St. Charles
                County — plus complete <strong>flooring removal</strong>,
                subfloor prep, and <strong>baseboard installation</strong>.
              </p>
              <p>
                Whether you&apos;re remodeling a bungalow in{" "}
                <strong>Webster Groves</strong>, updating a family home in{" "}
                <strong>O&apos;Fallon</strong>, refinishing a basement in{" "}
                <strong>Chesterfield</strong>, or gutting a bathroom in{" "}
                <strong>Central West End</strong> — every job gets the same
                obsessive precision on subfloor prep, tight seams, flush
                transitions, and a real workmanship guarantee.
              </p>
            </div>
          </div>
        </section>

        {/* SERVICES — deep sections, one per service */}
        <section
          id="services"
          data-testid="fc-services"
          className="py-16 lg:py-24 bg-stone-50"
        >
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="max-w-3xl mb-14">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-orange-600 font-semibold mb-6">
                <span className="h-px w-8 bg-orange-600" />
                Our Flooring Services
              </div>
              <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.025em] text-stone-900">
                Five services.
                <br />
                <span className="text-stone-500">One trusted crew.</span>
              </h2>
              <p className="mt-6 text-lg text-stone-600 leading-relaxed">
                From tear-out to trim — we handle every step of your flooring
                project across St. Louis and St. Charles County.
              </p>
            </div>

            <div className="space-y-16 lg:space-y-24">
              {SERVICES.map((s, i) => {
                const Icon = s.icon;
                return (
                  <article
                    key={s.id}
                    id={s.id}
                    data-testid={`fc-service-${s.id}`}
                    className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center scroll-mt-24"
                  >
                    <div
                      className={`lg:col-span-6 ${
                        i % 2 === 1 ? "lg:order-2" : ""
                      }`}
                    >
                      <div className="relative aspect-[4/3] overflow-hidden rounded-3xl bg-stone-200 shadow-[0_20px_60px_-25px_rgba(0,0,0,0.35)]">
                        <img
                          src={s.image}
                          alt={`${s.title} by M&L Exterior and Interior Design in Greater St. Louis`}
                          loading="lazy"
                          className="absolute inset-0 h-full w-full object-cover"
                        />
                      </div>
                    </div>

                    <div className="lg:col-span-6">
                      <div className="inline-flex items-center gap-2 text-xs font-bold text-orange-600 uppercase tracking-[0.15em] mb-4">
                        <Icon size={14} strokeWidth={2.25} />
                        Service · 0{i + 1}
                      </div>
                      <h3 className="font-display font-semibold text-3xl md:text-4xl lg:text-[42px] leading-[1.08] tracking-[-0.02em] text-stone-900">
                        {s.title}
                      </h3>
                      <p className="mt-3 text-sm font-semibold text-orange-600 tracking-wide">
                        {s.subtitle}
                      </p>

                      <div className="mt-6 space-y-4">
                        {s.body.map((p, idx) => (
                          <p
                            key={idx}
                            className="text-stone-700 leading-[1.75] text-[16px]"
                          >
                            {p}
                          </p>
                        ))}
                      </div>

                      <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                        {s.features.map((f) => (
                          <li
                            key={f}
                            className="flex items-start gap-2.5 text-sm text-stone-800"
                          >
                            <Check
                              size={16}
                              strokeWidth={2.5}
                              className="text-orange-600 mt-0.5 shrink-0"
                            />
                            {f}
                          </li>
                        ))}
                      </ul>

                      <a
                        href="#contact"
                        data-testid={`fc-service-cta-${s.id}`}
                        className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 bg-stone-900 text-stone-50 text-sm font-semibold rounded-full hover:bg-orange-600 transition-colors group"
                      >
                        Get a Quote for {s.title.split(" ")[0]}
                        <ArrowRight
                          size={15}
                          className="group-hover:translate-x-0.5 transition-transform"
                        />
                      </a>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* NEIGHBORHOODS — deep local SEO */}
        <section
          id="areas"
          data-testid="fc-areas"
          className="py-16 lg:py-24 bg-white"
        >
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="max-w-3xl mb-12">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-orange-600 font-semibold mb-6">
                <span className="h-px w-8 bg-orange-600" />
                Service Area · Missouri
              </div>
              <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.025em] text-stone-900">
                Flooring installation in every
                <br />
                <span className="text-stone-500">
                  St. Louis &amp; St. Charles neighborhood.
                </span>
              </h2>
              <p className="mt-6 text-lg text-stone-600 leading-relaxed">
                From historic homes in the City of St. Louis to new builds in
                St. Charles County — we install flooring across 40+
                communities.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              {/* St. Louis City */}
              <div
                data-testid="areas-stl-city"
                className="rounded-3xl border border-stone-200 bg-stone-50 p-7 lg:p-8"
              >
                <div className="text-xs uppercase tracking-[0.15em] text-orange-600 font-semibold mb-4">
                  City of St. Louis
                </div>
                <h3 className="font-display font-semibold text-2xl lg:text-3xl leading-tight text-stone-900 tracking-tight mb-5">
                  Flooring Contractor · St. Louis City Neighborhoods
                </h3>
                <p className="text-stone-600 text-[15px] leading-relaxed mb-5">
                  Historic homes and modern condos throughout the City of St.
                  Louis — we specialize in fitting new LVP, laminate, and tile
                  into homes with original character.
                </p>
                <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm text-stone-700">
                  {NEIGHBORHOODS_STL_CITY.map((n) => (
                    <li key={n} className="flex items-center gap-1.5">
                      <span className="w-1 h-1 bg-orange-500 rounded-full shrink-0" />
                      {n}
                    </li>
                  ))}
                </ul>
              </div>

              {/* St. Louis County */}
              <div
                data-testid="areas-stl-county"
                className="rounded-3xl border border-stone-200 bg-stone-50 p-7 lg:p-8"
              >
                <div className="text-xs uppercase tracking-[0.15em] text-orange-600 font-semibold mb-4">
                  St. Louis County
                </div>
                <h3 className="font-display font-semibold text-2xl lg:text-3xl leading-tight text-stone-900 tracking-tight mb-5">
                  Flooring Installation · St. Louis County
                </h3>
                <p className="text-stone-600 text-[15px] leading-relaxed mb-5">
                  From upscale remodels in Ladue and Clayton to family homes in
                  Kirkwood, Webster Groves, and Ballwin — we cover the full
                  county.
                </p>
                <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm text-stone-700">
                  {NEIGHBORHOODS_STL_COUNTY.map((n) => (
                    <li key={n} className="flex items-center gap-1.5">
                      <span className="w-1 h-1 bg-orange-500 rounded-full shrink-0" />
                      {n}
                    </li>
                  ))}
                </ul>
              </div>

              {/* St. Charles */}
              <div
                data-testid="areas-stcharles"
                className="rounded-3xl border border-stone-200 bg-stone-50 p-7 lg:p-8"
              >
                <div className="text-xs uppercase tracking-[0.15em] text-orange-600 font-semibold mb-4">
                  St. Charles County
                </div>
                <h3 className="font-display font-semibold text-2xl lg:text-3xl leading-tight text-stone-900 tracking-tight mb-5">
                  Flooring Contractor · St. Charles County
                </h3>
                <p className="text-stone-600 text-[15px] leading-relaxed mb-5">
                  Based in O&apos;Fallon — we cover every community in St. Charles
                  County, from Wentzville and Lake St. Louis to Cottleville
                  and New Melle.
                </p>
                <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm text-stone-700">
                  {NEIGHBORHOODS_STCHARLES.map((n) => (
                    <li key={n} className="flex items-center gap-1.5">
                      <span className="w-1 h-1 bg-orange-500 rounded-full shrink-0" />
                      {n}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* WHY LOCAL */}
        <section
          data-testid="fc-why"
          className="py-16 lg:py-24 bg-stone-900 text-stone-100 relative overflow-hidden"
        >
          <div
            aria-hidden
            className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full opacity-30 blur-[140px] pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, rgba(234,88,12,0.7) 0%, rgba(234,88,12,0) 70%)",
            }}
          />
          <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="max-w-3xl mb-12">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-orange-400 font-semibold mb-6">
                <span className="h-px w-8 bg-orange-400" />
                Why Homeowners Choose M&amp;L
              </div>
              <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.025em] text-stone-50">
                A St. Louis flooring contractor
                <br />
                <span className="text-orange-400">
                  who actually shows up — and stands by it.
                </span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  n: "01",
                  title: "Local, Owner-Operated",
                  copy: "Based in O'Fallon, MO. Same owner on every quote and every install — from Chesterfield to Wentzville.",
                },
                {
                  n: "02",
                  title: "Every Service In-House",
                  copy: "Tear-out, subfloor prep, LVP · laminate · tile install, baseboards, and finish trim — no sub-contractor game of telephone.",
                },
                {
                  n: "03",
                  title: "Workmanship Guarantee",
                  copy: "If a plank lifts or a tile cracks on a floor we installed, we come back. No fine print. No fees.",
                },
              ].map((p) => (
                <div
                  key={p.n}
                  className="bg-stone-800/60 backdrop-blur-sm border border-stone-700/50 p-8 rounded-2xl"
                >
                  <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-stone-50 font-bold text-sm mb-5">
                    {p.n}
                  </div>
                  <div className="font-display font-semibold text-2xl text-stone-50 leading-tight mb-3">
                    {p.title}
                  </div>
                  <p className="text-stone-400 leading-relaxed text-[15px]">
                    {p.copy}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS — reuse component */}
        <Testimonials />

        {/* FAQ */}
        <section
          id="faq"
          data-testid="fc-faq"
          className="py-16 lg:py-24 bg-stone-50"
        >
          <div className="max-w-[1000px] mx-auto px-6 lg:px-10">
            <div className="max-w-3xl mb-12">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-orange-600 font-semibold mb-6">
                <span className="h-px w-8 bg-orange-600" />
                Flooring Questions · Answered
              </div>
              <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.025em] text-stone-900">
                Flooring FAQs
                <br />
                <span className="text-stone-500">St. Louis &amp; St. Charles.</span>
              </h2>
            </div>

            <div className="divide-y divide-stone-200 border-y border-stone-200">
              {FAQS.map((f, i) => (
                <details
                  key={i}
                  data-testid={`fc-faq-${i}`}
                  className="group py-6"
                >
                  <summary className="flex items-start justify-between gap-6 cursor-pointer list-none">
                    <h3 className="font-display font-semibold text-lg lg:text-xl text-stone-900 leading-snug">
                      {f.q}
                    </h3>
                    <span className="shrink-0 w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-stone-700 group-open:bg-orange-500 group-open:text-stone-50 transition-colors">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        className="transition-transform group-open:rotate-45"
                      >
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    </span>
                  </summary>
                  <p className="mt-4 text-stone-700 text-[15px] leading-[1.75] max-w-3xl">
                    {f.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA + QUOTE */}
        <QuoteContact />
      </main>

      <Footer />
      <MobileStickyCTA />
    </div>
  );
}
